import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { DirectionsCar, Person, Build, Badge } from '@mui/icons-material';
import algolia from '../utilities/algolia';
import constants from '../utilities/constants';
import { useAutocompleteSlotProps } from '../utilities/ModalStackContext';

// Polymorphic entity links for files: { type, id, label }.
// `type` matches the Store linkKeys convention (`type:id`).
const TYPE_META = {
  car: { label: 'Car', color: 'primary', Icon: DirectionsCar },
  customer: { label: 'Customer', color: 'success', Icon: Person },
  serviceOrder: { label: 'Service Order', color: 'secondary', Icon: Build },
  user: { label: 'User', color: 'info', Icon: Badge },
};

// Users come from the static id→name map in constants (no Algolia index).
const USER_OPTIONS = Object.entries(constants.userNames).map(([id, name]) => ({
  type: 'user', id, label: name,
}));

const keyOf = (o) => `${o.type}:${o.id}`;

export default function EntityLinkPicker({
  value = [],
  onChange,
  label = 'Link to',
  placeholder = 'Search cars, customers, service orders, users…',
  size = 'small',
}) {
  const [input, setInput] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef();
  const reqRef = React.useRef(0);
  const slotProps = useAutocompleteSlotProps();

  React.useEffect(() => {
    const q = input.trim();
    clearTimeout(debounceRef.current);
    if (!q) { setOptions([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqRef.current;
      try {
        const { results } = await algolia.client.multipleQueries([
          { indexName: 'cars', query: q, hitsPerPage: 8 },
          { indexName: 'customers', query: q, hitsPerPage: 8 },
          { indexName: 'orders', query: q, hitsPerPage: 8 },
        ]);
        if (reqId !== reqRef.current) return;
        const cars = (results[0]?.hits || []).map(h => ({
          type: 'car',
          id: h.stock || h.objectID,
          label: `${h.stock || ''} — ${[h.year, h.make, h.model].filter(Boolean).join(' ')}`.trim(),
        }));
        const customers = (results[1]?.hits || []).map(h => ({
          type: 'customer',
          id: h.objectID,
          label: `${h.first_name || ''} ${h.last_name || ''}`.trim() || h.objectID,
        }));
        const orders = (results[2]?.hits || []).map(h => {
          const car = [h.year, h.make, h.model].filter(Boolean).join(' ');
          return {
            type: 'serviceOrder',
            id: h.stock || h.objectID,
            label: `SO ${h.stock || h.objectID}${car ? ` — ${car}` : ''}`,
          };
        });
        const users = USER_OPTIONS.filter(u => u.label.toLowerCase().includes(q.toLowerCase()));
        setOptions([...cars, ...customers, ...orders, ...users]);
      } catch (e) {
        console.warn('EntityLinkPicker search failed', e);
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [input]);

  // Merge current selections into the option list so Autocomplete can render them.
  const merged = React.useMemo(() => {
    const seen = new Set(value.map(keyOf));
    return [...value, ...options.filter(o => !seen.has(keyOf(o)))];
  }, [value, options]);

  return (
    <Autocomplete
      multiple
      size={size}
      filterSelectedOptions
      value={value}
      options={merged}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(o, v) => keyOf(o) === keyOf(v)}
      getOptionLabel={(o) => o.label || ''}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      slotProps={slotProps}
      renderOption={(props, option) => {
        const meta = TYPE_META[option.type] || {};
        const Icon = meta.Icon;
        return (
          <li {...props} key={keyOf(option)}>
            {Icon && <Icon fontSize="small" style={{ marginRight: 8, opacity: 0.6 }} />}
            <span style={{ flex: 1 }}>{option.label}</span>
            <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.5 }}>{meta.label}</span>
          </li>
        );
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => {
          const meta = TYPE_META[option.type] || {};
          const TagIcon = meta.Icon;
          return (
            <Chip
              size="small"
              color={meta.color || 'default'}
              variant="outlined"
              icon={TagIcon ? <TagIcon /> : undefined}
              label={option.label}
              {...getTagProps({ index })}
              key={keyOf(option)}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? undefined : placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
